// Ana menü (AppShell) — öğe listesi + çizim. AppShell'den ayrıldı (Finar UX P3 K1): aynı menü
// hem kenar kolonunda (240 px / 60 px ray) hem dar ekranın açılır menüsünde (overlay) çizilir.

import { NavLink } from "react-router-dom";
import {
  Archive,
  Building2,
  FileEdit,
  LayoutDashboard,
  FolderKanban,
  Palette,
  Settings,
  Shield,
  Users,
  type LucideIcon,
} from "lucide-react";
import type { Permissions } from "@mahir/shared";
import { railLinkLabel } from "../../lib/shell-a11y";

interface NavChild {
  to: string;
  label: string;
  requires?: keyof Permissions | null;
  // M13: NavLink end flag — sibling prefix overlap için (örn. /admin/frameworks
  // ve /admin/frameworks/sector-packages aynı anda active görünmesin).
  end?: boolean;
}

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  requires?: keyof Permissions | null;
  // M12 — expandable submenu. Parent route'a girince + path children'a match ederse açık.
  children?: NavChild[];
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  {
    to: "/projects",
    label: "Projeler",
    icon: FolderKanban,
    requires: "projects.view_assigned",
    // Geçmiş Rapor Aktarımı (B10/RG7): alt menü. Çocuk etiketi parent ile AYNI
    // OLAMAZ (e2e 01 strict-mode: "Projeler" iki kez eşleşirdi) → "Tüm projeler".
    children: [
      { to: "/projects", label: "Tüm projeler", end: true },
      {
        to: "/projects/import",
        label: "Geçmiş Rapor Aktarımı",
        requires: "projects.import_report",
      },
    ],
  },
  { to: "/archive", label: "Arşiv", icon: Archive, requires: "archive.view_assigned" },
  {
    to: "/clients",
    label: "Müşteriler",
    icon: Building2,
    requires: "clients.list_all",
    // M16 KPI (RK20): alt menü — /clients/kpi/* isParentActive startsWith("/clients") ile parent'ı
    // boyar; bilinçli yan etki: /clients/new ve /clients/:id de Müşteriler'i aktif gösterir.
    // Çocuk etiketi parent ile AYNI OLAMAZ (01 spec strict-mode) → "Tüm müşteriler".
    children: [
      { to: "/clients", label: "Tüm müşteriler", end: true },
      { to: "/clients/kpi/ozet", label: "Performans Özeti", requires: "kpi.view" },
      { to: "/clients/kpi/tablo", label: "Veri Girişi", requires: "kpi.view" },
      { to: "/clients/kpi/import", label: "İçe Aktar", requires: "kpi.edit" },
      { to: "/clients/kpi/setler", label: "Veri Yönetimi", requires: "kpi.manage" },
      { to: "/clients/kpi/paketler", label: "Paketler", requires: "kpi.manage" },
      { to: "/clients/kpi/katalog", label: "Katalog", requires: "kpi.view" },
      { to: "/clients/kpi/hedefler", label: "Hedefler", requires: "kpi.view" },
      { to: "/clients/kpi/disclosure", label: "Disclosure Hazırlık", requires: "kpi.view" },
    ],
  },
  { to: "/admin/users", label: "Kullanıcılar", icon: Users, requires: "users.invite" },
  { to: "/admin/roles", label: "Roller", icon: Shield, requires: "system.admin" },
  {
    to: "/admin/frameworks",
    label: "Çerçeve Yöneticisi",
    icon: FileEdit,
    requires: "framework.create",
    // M13 sub-menu: Frameworks (mevcut /admin/frameworks) + Sektör Paketleri
    // (yeni /admin/frameworks/sector-packages). isParentActive startsWith
    // logic'ine uymak için ikinci path /admin/frameworks/* altında nested.
    children: [
      { to: "/admin/frameworks", label: "Çerçeveler", end: true },
      {
        to: "/admin/frameworks/sector-packages",
        label: "Sektör Paketleri",
        requires: "sector_packages.edit",
      },
    ],
  },
  { to: "/admin/branding", label: "Branding", icon: Palette, requires: "system.admin" },
  {
    to: "/admin/settings",
    label: "Sistem Ayarları",
    icon: Settings,
    requires: "system.admin",
    children: [
      { to: "/admin/settings/llm", label: "LLM Bağlantıları" },
      { to: "/admin/settings/mail", label: "Mail Ayarları" },
      { to: "/admin/settings/prompt", label: "Sistem Prompt" },
      {
        to: "/admin/settings/prompt-library",
        label: "KK Prompt Library",
        requires: "qc_prompts.manage",
      },
      { to: "/admin/settings/budget", label: "Bütçe" },
      { to: "/admin/settings/ui", label: "Arayüz" },
      { to: "/admin/settings/evidence", label: "Kanıt Belgeleri" },
      { to: "/admin/settings/audit", label: "Audit Log" },
    ],
  },
];

interface SidebarNavProps {
  collapsed: boolean;
  permissions: Permissions;
  pathname: string;
  /** Landmark adı — açılır menüde kenar kolonundakinden ayrı ad (landmark-unique). */
  label?: string;
  /**
   * Açılır menü (dar ekran): alt öğeler her zaman görünür — üst bağlantıya tıklamak rotayı
   * değiştirip menüyü kapattığı için alt sayfaya ikinci kez açmadan ulaşılabilsin.
   */
  showAllChildren?: boolean;
}

export function SidebarNav({
  collapsed,
  permissions,
  pathname,
  label = "Ana menü",
  showAllChildren = false,
}: SidebarNavProps): JSX.Element {
  return (
    <nav className="flex-1 px-3" aria-label={label}>
      {NAV_ITEMS.filter((item) => !item.requires || permissions[item.requires] === true).map(
        (item) => {
          const Icon = item.icon;
          // Submenu açık ise: parent route'un altındaki herhangi bir path aktif
          const isParentActive = !!item.children && pathname.startsWith(item.to);
          return (
            <div key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === "/"}
                title={collapsed ? item.label : undefined}
                // İ5 — daraltılmış rail'de görünür metin yok; ad title'a bırakılmaz.
                aria-label={railLinkLabel(collapsed, item.label)}
                style={({ isActive }) => ({
                  display: "flex",
                  alignItems: "center",
                  gap: collapsed ? 0 : 12,
                  justifyContent: collapsed ? "center" : "flex-start",
                  padding: collapsed ? "10px 0" : "10px 14px",
                  marginBottom: 4,
                  borderRadius: 8,
                  fontFamily: "var(--font-sans)",
                  fontSize: "0.95rem",
                  color:
                    isActive || isParentActive ? "var(--color-primary)" : "var(--color-text-2)",
                  background:
                    isActive || isParentActive
                      ? "color-mix(in srgb, var(--color-primary) 14%, white)"
                      : "transparent",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                })}
              >
                <Icon size={18} aria-hidden="true" style={{ flexShrink: 0 }} />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
              {/* M12 — submenu (children). Sadece parent route aktifse + sidebar açıkken görünür */}
              {item.children && (isParentActive || showAllChildren) && !collapsed && (
                <div style={{ marginLeft: 32, marginBottom: 6 }}>
                  {item.children
                    .filter((ch) => !ch.requires || permissions[ch.requires] === true)
                    .map((ch) => (
                      <NavLink
                        key={ch.to}
                        to={ch.to}
                        end={ch.end === true}
                        style={({ isActive }) => ({
                          display: "block",
                          padding: "6px 14px",
                          marginBottom: 2,
                          borderRadius: 6,
                          fontSize: "0.85rem",
                          color: isActive ? "var(--color-primary)" : "var(--color-text-3)",
                          background: isActive
                            ? "color-mix(in srgb, var(--color-primary) 10%, white)"
                            : "transparent",
                          textDecoration: "none",
                          fontWeight: isActive ? 600 : 400,
                        })}
                      >
                        {ch.label}
                      </NavLink>
                    ))}
                </div>
              )}
            </div>
          );
        },
      )}
    </nav>
  );
}
